// organization.effects.ts
import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { BlockUI, NgBlockUI } from "ng-block-ui";
import { MessageService } from "primeng/api";
import { catchError, map, mergeMap, of } from "rxjs";
import { RestService } from "../../services/rest.service";
import { ToasterService } from "../../services/toaster-service";
import { MessageEnum } from "../../utils/message.enum";
import { ProjectActions } from "../actions/project.actions";

@Injectable()
export class ProjectEffects {
  @BlockUI() blockUI!: NgBlockUI;
  private messageService = inject(MessageService);

  constructor(private actions$: Actions, private restService: RestService, private readonly toaster: ToasterService) {}

  loadProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectActions.loadProject),
      mergeMap(() =>
        this.restService.getProject().pipe(
          map((response: any) => {
            this.blockUI.stop();
            const projectList = response.data.sort((a: any, b: any) => {
              return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
            });
            return ProjectActions.loadProjectSuccess({
              projectList,
            });
          }),
          catchError((error) => {
            this.toaster.pop({ type: 'error', text: MessageEnum.PROJECT_LOAD_FAILURE });
            this.blockUI.stop();
            return of(ProjectActions.loadProjectFailure({ error }));
          })
        )
      )
    )
  );

  createProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectActions.createProject),
      mergeMap((action) =>
        this.restService.createProject(action.project).pipe(
          map((newProject: any) => {
            this.toaster.pop({ type: 'success', text: MessageEnum.PROJECT_CREATE_SUCCESS });
            this.blockUI.stop();
            const projectData = newProject?.data;
            return ProjectActions.createProjectSuccess({
              project: projectData,
            });
          }),
          catchError((error) => {
            this.blockUI.stop();
            this.toaster.pop({ type: 'error', text: MessageEnum.PROJECT_CREATE_FAILURE });
            return of(ProjectActions.createProjectFailure({ error }));
          })
        )
      )
    )
  );

  editProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectActions.editProject),
      mergeMap((action) =>
        this.restService.updateProject(action.project,action.projectId).pipe(
          map((updatedProject) => {
            this.toaster.pop({ type: 'success', text: MessageEnum.PROJECT_UPDATE_SUCCESS });
            this.blockUI.stop();
            return ProjectActions.editProjectSuccess({
              project: updatedProject,
            });
          }),
          catchError((error) => {
            this.blockUI.stop();
            this.toaster.pop({ type: 'error', text: MessageEnum.PROJECT_UPDATE_FAILURE });
            return of(ProjectActions.loadProjectFailure({ error }));
          })
        )
      )
    )
  );

  deleteProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectActions.deleteProject),
      mergeMap((action) =>
        this.restService.deleteProject(action.projectId).pipe(
          map(() => {
            this.blockUI.stop();
            this.toaster.pop({ type: 'success', text: MessageEnum.PROJECT_DELETE_SUCCESS });
            return ProjectActions.deleteProjectSuccess({
              projectId: action.projectId,
            });
          }),
          catchError((error) => {
            this.blockUI.stop();
            this.toaster.pop({ type: 'error', text: MessageEnum.PROJECT_DELETE_FAILURE });
            return of(ProjectActions.loadProjectFailure({ error }));
          })
        )
      )
    )
  );
}
